import { useQueryClient } from "@tanstack/react-query";
import { doc, getFirestore, runTransaction } from "firebase/firestore";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { queryKeys } from "../../queries/queryKeys";
import {
  aplicarGastoARutaLocal,
  construirActualizacionRutaConGasto,
  crearGastoRutaRef,
} from "../../utils/rutaGastos";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../utils/rutaMovimientos";
import type { GastoFormEvent, GastoInputChangeEvent, UseGastosDiariosParams } from "./types";

type ValorGastoInput = number | "";

export const calcularSaldoNuevo = (saldoInicial: number | string, valor: number): number => {
  return Number(saldoInicial ?? 0) - valor;
};

export const formularioGastoEsValido = (
  valor: ValorGastoInput,
  descripcion: string,
): valor is number => {
  return (
    typeof valor === "number" &&
    Number.isFinite(valor) &&
    valor > 0 &&
    descripcion.trim().length > 0
  );
};

export const useGastosDiarios = ({ usuarioRuta, setUsuarioRuta }: UseGastosDiariosParams) => {
  const [valor, setValor] = useState<ValorGastoInput>("");
  const [descripcion, setDescripcion] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const queryClient = useQueryClient();
  const hasUsuarioRuta = Boolean(usuarioRuta?.adminUid && usuarioRuta?.uid);

  const canSubmit = useMemo(
    () => formularioGastoEsValido(valor, descripcion) && !isAdding && hasUsuarioRuta,
    [descripcion, hasUsuarioRuta, isAdding, valor],
  );

  const handleValorChange = useCallback((event: GastoInputChangeEvent) => {
    const nextValue = event.target.value;
    setValor(nextValue === "" ? "" : Number(nextValue));
  }, []);

  const handleDescripcionChange = useCallback((event: GastoInputChangeEvent) => {
    setDescripcion(event.target.value);
  }, []);

  const resetForm = useCallback(() => {
    setValor("");
    setDescripcion("");
  }, []);

  const agregarGasto = useCallback(
    async (event: GastoFormEvent) => {
      event.preventDefault();

      if (isAdding) {
        return;
      }

      if (!formularioGastoEsValido(valor, descripcion)) {
        toast.error("Debes ingresar un valor valido y una descripcion.");
        return;
      }

      if (!usuarioRuta?.adminUid || !usuarioRuta?.uid) {
        toast.error("La informacion de la ruta aun no esta disponible.");
        return;
      }

      const valorGasto = valor;

      try {
        setIsAdding(true);
        const db = getFirestore();
        const rutaRef = doc(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid);
        const nuevoGasto = {
          descripcion: descripcion.trim(),
          fecha: new Date(),
          valor: valorGasto,
        };
        const gastoRef = crearGastoRutaRef(db, usuarioRuta.adminUid, usuarioRuta.uid);
        const movimientoRef = crearMovimientoRutaRef(db, usuarioRuta.adminUid, usuarioRuta.uid);

        const resultadoTransaccion = await runTransaction(db, async (transaction) => {
          const rutaSnapshot = await transaction.get(rutaRef);
          const rutaData = rutaSnapshot.data();

          if (!rutaData) {
            throw new Error("No se pudo obtener la ruta actual.");
          }

          const saldoNuevo = calcularSaldoNuevo(rutaData.saldoInicial, valorGasto);
          const movimientoGasto = crearMovimientoRutaData({
            descripcion: "Gasto D",
            fecha: nuevoGasto.fecha,
            monto: valorGasto,
            origen: "gasto_diario",
            responsable: rutaData.responsable ?? "Admin",
            saldoDespues: saldoNuevo,
          });
          const rutaUpdates = {
            ...construirActualizacionRutaConGasto(rutaData, nuevoGasto.fecha),
            ...construirActualizacionRutaConMovimiento(rutaData, movimientoGasto, saldoNuevo),
          };

          transaction.set(gastoRef, nuevoGasto);
          transaction.set(movimientoRef, movimientoGasto);
          transaction.update(rutaRef, rutaUpdates);

          return {
            movimientoGasto,
            nuevoGasto,
            saldoNuevo,
          };
        });

        setUsuarioRuta((previousUsuarioRuta) => {
          if (!previousUsuarioRuta) {
            return previousUsuarioRuta;
          }

          const rutaActualizada = aplicarGastoARutaLocal(
            aplicarMovimientoARutaLocal(
              previousUsuarioRuta,
              resultadoTransaccion.movimientoGasto,
              resultadoTransaccion.saldoNuevo,
            ),
            resultadoTransaccion.nuevoGasto.fecha,
          );

          if (!Object.prototype.hasOwnProperty.call(rutaActualizada, "historialGastos")) {
            return rutaActualizada;
          }

          const rutaSinHistorialLegacy = { ...rutaActualizada };

          delete rutaSinHistorialLegacy.historialGastos;

          return rutaSinHistorialLegacy;
        });

        // Colapsa la cache paginada a la 1ra pagina antes de invalidar, para
        // que el refetch solo traiga esa pagina en vez de repetir todas las
        // que el usuario ya hubiera cargado con "Cargar mas" en la sesion.
        queryClient.setQueryData(
          queryKeys.gastos(usuarioRuta.adminUid, usuarioRuta.uid),
          (previousData: { pageParams: unknown[]; pages: unknown[] } | undefined) =>
            previousData
              ? {
                  pageParams: previousData.pageParams.slice(0, 1),
                  pages: previousData.pages.slice(0, 1),
                }
              : previousData,
        );
        void queryClient.invalidateQueries({
          queryKey: queryKeys.gastos(usuarioRuta.adminUid, usuarioRuta.uid),
        });
        // El gasto tambien escribe un movimiento de caja ("Gasto D") y altera
        // el total de "gastos del dia" que muestra la pagina de Estadisticas.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientos(usuarioRuta.adminUid, usuarioRuta.uid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientosDelDiaByRuta(usuarioRuta.adminUid, usuarioRuta.uid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.gastosDelDiaByRuta(usuarioRuta.adminUid, usuarioRuta.uid),
        });

        toast.success("Gasto agregado con exito");
        resetForm();
      } catch (error: any) {
        console.error("Error al agregar el gasto:", error?.message);
      } finally {
        setIsAdding(false);
      }
    },
    [descripcion, isAdding, queryClient, resetForm, setUsuarioRuta, usuarioRuta, valor],
  );

  return {
    valor,
    descripcion,
    isAdding,
    canSubmit,
    handleValorChange,
    handleDescripcionChange,
    agregarGasto,
  };
};
